import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faSync, faCheckCircle, faExclamationTriangle, faExclamationCircle, faSave, faEye } from '@fortawesome/free-solid-svg-icons';
import { Breadcrumb, Card, Button, Row, Col, Alert, Spinner, Form, InputGroup, Modal, Table, Badge } from '@themesberg/react-bootstrap';
import { useCookies } from 'react-cookie';
import { toast } from 'react-toastify';
import { API_URL_CRAWLER } from '../../../config';
import {
  getClassicPeriodAutoCreateSetting,
  getDienToanCrawlerSetting,
  updateClassicPeriodAutoCreateSetting,
  updateDienToanCrawlerSetting,
} from '../../../controller/api/setting';

const MIN_PERIOD_DAYS_AHEAD = 1;
const MAX_PERIOD_DAYS_AHEAD = 30;

const CrawlerSettings = () => {
  const [loading, setLoading] = useState({});
  const [messages, setMessages] = useState({});
  const [dienToanBaseUrl, setDienToanBaseUrl] = useState('');
  const [dienToanInput, setDienToanInput] = useState('');
  const [dienToanLoading, setDienToanLoading] = useState(false);
  const [dienToanSaving, setDienToanSaving] = useState(false);
  const [periodAutoCreateEnabled, setPeriodAutoCreateEnabled] = useState(false);
  const [periodDaysAheadInput, setPeriodDaysAheadInput] = useState('');
  const [periodAutoCreateLoading, setPeriodAutoCreateLoading] = useState(false);
  const [periodAutoCreateSaving, setPeriodAutoCreateSaving] = useState(false);
  const [cookies] = useCookies(['lottmekong']);
  const token = cookies.lottmekong;

  const parsedPeriodDaysAhead = Number(periodDaysAheadInput);
  const isPeriodDaysAheadInvalid =
    periodDaysAheadInput === '' ||
    !Number.isInteger(parsedPeriodDaysAhead) ||
    parsedPeriodDaysAhead < MIN_PERIOD_DAYS_AHEAD ||
    parsedPeriodDaysAhead > MAX_PERIOD_DAYS_AHEAD;

  const [previewState, setPreviewState] = useState({
    show: false,
    type: null,
    title: '',
    baseUrl: '',
    data: null,
    loading: false,
    error: null,
    applying: false,
  });

  const loadDienToanSetting = useCallback(async () => {
    if (!token) return;
    setDienToanLoading(true);
    try {
      const cfg = await getDienToanCrawlerSetting(token);
      const url = cfg?.baseUrl || '';
      setDienToanBaseUrl(url);
      setDienToanInput(url);
    } catch (e) {
      console.error('load dien-toan crawler setting', e);
    } finally {
      setDienToanLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadDienToanSetting();
  }, [loadDienToanSetting]);

  const saveDienToanSetting = async () => {
    if (!token) return;
    setDienToanSaving(true);
    try {
      const cfg = await updateDienToanCrawlerSetting(token, { baseUrl: dienToanInput.trim() });
      const url = cfg?.baseUrl || '';
      setDienToanBaseUrl(url);
      setDienToanInput(url);
      toast.success('Đã lưu URL crawler điện toán');
    } catch (e) {
      console.error('save dien-toan crawler setting', e);
      toast.error('Lưu thất bại: ' + (e?.response?.data?.message || e.message));
    } finally {
      setDienToanSaving(false);
    }
  };

  const loadPeriodAutoCreateSetting = useCallback(async () => {
    if (!token) return;
    setPeriodAutoCreateLoading(true);
    try {
      const cfg = await getClassicPeriodAutoCreateSetting(token);
      setPeriodAutoCreateEnabled(cfg?.enabled === true);
      setPeriodDaysAheadInput(cfg?.daysAhead != null ? String(cfg.daysAhead) : '');
    } catch (e) {
      console.error('load classic period auto create setting', e);
    } finally {
      setPeriodAutoCreateLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPeriodAutoCreateSetting();
  }, [loadPeriodAutoCreateSetting]);

  const savePeriodAutoCreateSetting = async () => {
    if (!token || isPeriodDaysAheadInvalid) return;
    setPeriodAutoCreateSaving(true);
    try {
      const cfg = await updateClassicPeriodAutoCreateSetting(token, {
        enabled: periodAutoCreateEnabled,
        daysAhead: parsedPeriodDaysAhead,
      });
      setPeriodAutoCreateEnabled(cfg?.enabled === true);
      setPeriodDaysAheadInput(cfg?.daysAhead != null ? String(cfg.daysAhead) : '');
      toast.success('Đã lưu cấu hình tự động tạo kỳ kiến thiết');
    } catch (e) {
      console.error('save classic period auto create setting', e);
      toast.error('Lưu thất bại: ' + (e?.response?.data?.message || e.message));
    } finally {
      setPeriodAutoCreateSaving(false);
    }
  };

  const openPreview = async (type, title, baseUrl = API_URL_CRAWLER) => {
    if (!baseUrl) {
      setMessages(prev => ({
        ...prev,
        [type]: { type: 'danger', text: 'Chưa cấu hình URL crawler' }
      }));
      return;
    }

    setPreviewState({
      show: true,
      type,
      title,
      baseUrl,
      data: null,
      loading: true,
      error: null,
      applying: false,
    });

    try {
      const response = await fetch(`${baseUrl}/api/preview/${type}`, { method: 'GET' });
      const data = await response.json();
      if (!response.ok) {
        setPreviewState(prev => ({
          ...prev,
          loading: false,
          error: data.error || `HTTP ${response.status}`,
        }));
        return;
      }
      setPreviewState(prev => ({ ...prev, loading: false, data }));
    } catch (error) {
      setPreviewState(prev => ({ ...prev, loading: false, error: error.message }));
    }
  };

  const closePreview = () => {
    setPreviewState(prev => ({ ...prev, show: false }));
  };

  // Các game có endpoint /force-sync (ghi đồng bộ + Force=true). xsmn/xsmt chỉ
  // có /crawl nên fallback. Force-sync cần thiết vì /crawl chạy bất đồng bộ
  // (trả "triggered" ngay) và Force=false → không ghi đè được kỳ đã chốt.
  const FORCE_SYNC_TYPES = ['keno', 'mega', 'power', 'max3d', 'max3dpro', 'lotto535', 'xsdt636'];

  const applyPreview = async () => {
    const { type, baseUrl } = previewState;
    if (!type || !baseUrl) return;
    setPreviewState(prev => ({ ...prev, applying: true }));
    setMessages(prev => ({ ...prev, [type]: null }));

    const useForceSync = FORCE_SYNC_TYPES.includes(type);
    const endpoint = useForceSync ? `${baseUrl}/api/force-sync/${type}` : `${baseUrl}/api/crawl/${type}`;

    try {
      const response = await fetch(endpoint, { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        let text = 'Đã lưu kết quả vào DB thành công!';
        // Force-sync trả về kết quả thật đã ghi → hiển thị để xác nhận.
        if (useForceSync && data.data) {
          const g = data.data;
          const resultText = g.result ? g.result.join(', ') : 'N/A';
          const jackpotText = g.jackpot ? g.jackpot.toLocaleString() : 'N/A';
          text = `Đã lưu kỳ ${g.periodId} | KQ: ${resultText} | Jackpot: ${jackpotText}`;
        }
        setMessages(prev => ({ ...prev, [type]: { type: 'success', text } }));
        setPreviewState(prev => ({ ...prev, show: false, applying: false }));
      } else {
        setMessages(prev => ({
          ...prev,
          [type]: { type: 'danger', text: data.error || 'Lỗi khi lưu kết quả' }
        }));
        setPreviewState(prev => ({ ...prev, applying: false }));
      }
    } catch (error) {
      setMessages(prev => ({
        ...prev,
        [type]: { type: 'danger', text: `Lỗi: ${error.message}` }
      }));
      setPreviewState(prev => ({ ...prev, applying: false }));
    }
  };

  const forceSync = async (type) => {
    const key = `force-${type}`;
    setLoading(prev => ({ ...prev, [key]: true }));
    setMessages(prev => ({ ...prev, [key]: null }));

    try {
      const response = await fetch(`${API_URL_CRAWLER}/api/force-sync/${type}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (response.ok || response.status === 207) {
        if (type === 'all') {
          // Handle /all endpoint with results and errors
          let messageText = '';
          
          if (data.data.results && data.data.results.length > 0) {
            messageText += data.data.results.map(item => 
              `✓ ${item.game}: Kỳ ${item.periodId}`
            ).join('\n');
          }
          
          if (data.data.errors && data.data.errors.length > 0) {
            if (messageText) messageText += '\n';
            messageText += data.data.errors.map(item =>
              `✗ ${item.game}: ${item.error}`
            ).join('\n');
          }

          setMessages(prev => ({
            ...prev,
            [key]: { type: 'success', text: messageText }
          }));
        } else {
          // Handle single game response
          const { data: gameData } = data;
          const resultText = gameData.result ? gameData.result.join(', ') : 'N/A';
          const jackpotText = gameData.jackpot ? gameData.jackpot.toLocaleString() : 'N/A';
          const messageText = `Kỳ: ${gameData.periodId} | KQ: ${resultText} | Jackpot: ${jackpotText}`;

          setMessages(prev => ({
            ...prev,
            [key]: { type: 'success', text: messageText }
          }));
        }
      } else {
        setMessages(prev => ({
          ...prev,
          [key]: { type: 'danger', text: data.error || 'Lỗi khi force sync' }
        }));
      }
    } catch (error) {
      setMessages(prev => ({
        ...prev,
        [key]: { type: 'danger', text: `Lỗi: ${error.message}` }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const crawlers = [
    {
      id: 'keno',
      title: 'Keno',
      description: 'Lấy kết quả Keno từ Vietlott',
      schedule: 'Mỗi phút từ 6h-22h (chạy 2 lần)',
      category: 'vietlott',
    },
    {
      id: 'mega',
      title: 'Mega 6/45',
      description: 'Lấy kết quả Mega 6/45 từ Vietlott',
      schedule: '19:00, 19:05, 19:10, 19:30 (CN, T4, T6)',
      category: 'vietlott',
    },
    {
      id: 'power',
      title: 'Power 6/55',
      description: 'Lấy kết quả Power 6/55 từ Vietlott',
      schedule: '19:00, 19:05, 19:10, 19:30 (T3, T5, T7)',
      category: 'vietlott',
    },
    {
      id: 'max3d',
      title: 'Max3D',
      description: 'Lấy kết quả Max3D từ Vietlott',
      schedule: '19:00, 19:05, 19:10, 19:30 (T2, T4, T6)',
      category: 'vietlott',
    },
    {
      id: 'max3dpro',
      title: 'Max3D Pro',
      description: 'Lấy kết quả Max3D Pro từ Vietlott',
      schedule: '19:00, 19:05, 19:10, 19:30 (T3, T5, T7)',
      category: 'vietlott',
    },
    {
      id: 'lotto535',
      title: 'Lotto 5/35',
      description: 'Lấy kết quả Lotto 5/35 từ Vietlott',
      schedule: '13:00 & 21:00 hàng ngày (chạy nhiều lần sau giờ quay)',
      category: 'vietlott',
    },
    {
      id: 'xsmn',
      title: 'XSMN - Xổ số Miền Nam',
      description: 'Lấy kết quả xổ số Miền Nam từ xosodaiphat.com',
      schedule: '16:30, 16:35, 16:40, 16:45, 17:00',
      category: 'classic',
    },
    {
      id: 'xsmt',
      title: 'XSMT - Xổ số Miền Trung',
      description: 'Lấy kết quả xổ số Miền Trung từ xosodaiphat.com',
      schedule: '17:30, 17:35, 17:40, 17:45, 18:00',
      category: 'classic',
    },
    {
      id: 'xsdt636',
      title: 'Điện toán 6x36',
      description: 'Lấy kết quả xổ số điện toán 6/36 từ daithantai.vn',
      schedule: 'Thứ 3, Thứ 5, Thứ 7, Chủ nhật lúc 18:00',
      category: 'dientoan',
      baseUrl: dienToanBaseUrl,
    },
  ];

  const forceSyncGames = [
    { id: 'keno', title: 'Keno' },
    { id: 'mega', title: 'Mega 6/45' },
    { id: 'power', title: 'Power 6/55' },
    { id: 'max3d', title: 'Max3D' },
    { id: 'max3dpro', title: 'Max3D Pro' },
    { id: 'lotto535', title: 'Lotto 5/35' },
  ];

  return (
    <>
      <div className="d-xl-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-xl-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
            <Breadcrumb.Item>Cài đặt</Breadcrumb.Item>
            <Breadcrumb.Item active>Crawler kết quả</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Quản lý Crawler Kết quả Xổ số</h4>
          <p className="mb-0">Trigger thủ công crawler khi cronjob thất bại</p>
        </div>
      </div>

      <h5 className="mb-3">Vietlott</h5>
      <Row>
        {crawlers.filter(c => c.category === 'vietlott').map((crawler) => (
          <Col key={crawler.id} xs={12} md={6} lg={4} className="mb-4">
            <Card className="h-100">
              <Card.Body>
                <Card.Title>{crawler.title}</Card.Title>
                <Card.Text className="text-muted small">
                  {crawler.description}
                </Card.Text>
                <Card.Text className="small">
                  <strong>Lịch chạy:</strong> {crawler.schedule}
                </Card.Text>

                {messages[crawler.id] && (
                  <Alert
                    variant={messages[crawler.id].type}
                    className="py-2 px-3 small"
                    dismissible
                    onClose={() => setMessages(prev => ({ ...prev, [crawler.id]: null }))}
                  >
                    <FontAwesomeIcon
                      icon={messages[crawler.id].type === 'success' ? faCheckCircle : faExclamationTriangle}
                      className="me-2"
                    />
                    {messages[crawler.id].text}
                  </Alert>
                )}

                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => openPreview(crawler.id, crawler.title, crawler.baseUrl)}
                  disabled={previewState.loading && previewState.type === crawler.id}
                >
                  <FontAwesomeIcon icon={faEye} className="me-2" />
                  Xem & Lấy kết quả
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <h5 className="mb-3 mt-4">Xổ số Kiến thiết</h5>
      <Row>
        {crawlers.filter(c => c.category === 'classic').map((crawler) => (
          <Col key={crawler.id} xs={12} md={6} lg={4} className="mb-4">
            <Card className="h-100">
              <Card.Body>
                <Card.Title>{crawler.title}</Card.Title>
                <Card.Text className="text-muted small">
                  {crawler.description}
                </Card.Text>
                <Card.Text className="small">
                  <strong>Lịch chạy:</strong> {crawler.schedule}
                </Card.Text>

                {messages[crawler.id] && (
                  <Alert
                    variant={messages[crawler.id].type}
                    className="py-2 px-3 small"
                    dismissible
                    onClose={() => setMessages(prev => ({ ...prev, [crawler.id]: null }))}
                  >
                    <FontAwesomeIcon
                      icon={messages[crawler.id].type === 'success' ? faCheckCircle : faExclamationTriangle}
                      className="me-2"
                    />
                    {messages[crawler.id].text}
                  </Alert>
                )}

                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => openPreview(crawler.id, crawler.title, crawler.baseUrl)}
                  disabled={previewState.loading && previewState.type === crawler.id}
                >
                  <FontAwesomeIcon icon={faEye} className="me-2" />
                  Xem & Lấy kết quả
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
       </Row>

       <Row>
         <Col xs={12} className="mb-4">
           <Card>
             <Card.Body>
               <Card.Title>Tự động tạo kỳ xổ số kiến thiết</Card.Title>
               <Card.Text className="text-muted small mb-3">
                 Hệ thống tự tạo kỳ mở thưởng cho tất cả đài MB, MT, MN vào 00:00 mỗi ngày và ngay khi khởi động lại server. Tạo trước càng nhiều ngày thì càng nhập vé sớm được nhiều kỳ.
               </Card.Text>
               <Row className="align-items-end">
                 <Col xs={12} md={4} className="mb-3 mb-md-0">
                   <Form.Check
                     type="switch"
                     id="classic-period-auto-create-enabled"
                     label={periodAutoCreateEnabled ? 'Đang bật' : 'Đang tắt'}
                     checked={periodAutoCreateEnabled}
                     onChange={(e) => setPeriodAutoCreateEnabled(e.target.checked)}
                     disabled={periodAutoCreateLoading || periodAutoCreateSaving}
                   />
                 </Col>
                 <Col xs={12} md={4} className="mb-3 mb-md-0">
                   <Form.Label>Số ngày tạo trước</Form.Label>
                   <Form.Control
                     type="number"
                     min={MIN_PERIOD_DAYS_AHEAD}
                     max={MAX_PERIOD_DAYS_AHEAD}
                     step="1"
                     value={periodDaysAheadInput}
                     onChange={(e) => setPeriodDaysAheadInput(e.target.value)}
                     isInvalid={isPeriodDaysAheadInvalid}
                     disabled={periodAutoCreateLoading || periodAutoCreateSaving}
                   />
                   <Form.Control.Feedback type="invalid">
                     Vui lòng nhập số nguyên từ {MIN_PERIOD_DAYS_AHEAD} đến {MAX_PERIOD_DAYS_AHEAD}.
                   </Form.Control.Feedback>
                 </Col>
                 <Col xs={12} md={4}>
                   <Button
                     variant="success"
                     className="w-100"
                     onClick={savePeriodAutoCreateSetting}
                     disabled={periodAutoCreateLoading || periodAutoCreateSaving || isPeriodDaysAheadInvalid}
                   >
                     {periodAutoCreateSaving ? (
                       <>
                         <Spinner animation="border" size="sm" className="me-2" />
                         Đang lưu...
                       </>
                     ) : (
                       <>
                         <FontAwesomeIcon icon={faSave} className="me-2" />
                         Lưu cấu hình
                       </>
                     )}
                   </Button>
                 </Col>
               </Row>
             </Card.Body>
           </Card>
         </Col>
       </Row>

       <h5 className="mb-3 mt-4">Xổ số Điện toán</h5>
       <Row>
         <Col xs={12} className="mb-3">
           <Card>
             <Card.Body>
               <Card.Title>Cấu hình URL crawler điện toán</Card.Title>
               <Card.Text className="text-muted small mb-2">
                 URL gốc của Go service (vd: <code>http://localhost:8081</code> hoặc <code>https://crawler-dientoan.veso79.vn</code>). Nút "Lấy kết quả ngay" bên dưới sẽ POST vào <code>{'{URL}/api/crawl/xsdt636'}</code>.
               </Card.Text>
               <InputGroup>
                 <Form.Control
                   type="url"
                   placeholder="https://crawler-dientoan.veso79.vn"
                   value={dienToanInput}
                   onChange={(e) => setDienToanInput(e.target.value)}
                   disabled={dienToanLoading || dienToanSaving}
                 />
                 <Button
                   variant="success"
                   onClick={saveDienToanSetting}
                   disabled={dienToanLoading || dienToanSaving || dienToanInput.trim() === dienToanBaseUrl}
                 >
                   {dienToanSaving ? (
                     <><Spinner animation="border" size="sm" className="me-2" />Đang lưu...</>
                   ) : (
                     <><FontAwesomeIcon icon={faSave} className="me-2" />Lưu</>
                   )}
                 </Button>
               </InputGroup>
             </Card.Body>
           </Card>
         </Col>
         {crawlers.filter(c => c.category === 'dientoan').map((crawler) => (
           <Col key={crawler.id} xs={12} md={6} lg={4} className="mb-4">
             <Card className="h-100">
               <Card.Body>
                 <Card.Title>{crawler.title}</Card.Title>
                 <Card.Text className="text-muted small">
                   {crawler.description}
                 </Card.Text>
                 <Card.Text className="small">
                   <strong>Lịch chạy:</strong> {crawler.schedule}
                 </Card.Text>

                 {messages[crawler.id] && (
                   <Alert
                     variant={messages[crawler.id].type}
                     className="py-2 px-3 small"
                     dismissible
                     onClose={() => setMessages(prev => ({ ...prev, [crawler.id]: null }))}
                   >
                     <FontAwesomeIcon
                       icon={messages[crawler.id].type === 'success' ? faCheckCircle : faExclamationTriangle}
                       className="me-2"
                     />
                     {messages[crawler.id].text}
                   </Alert>
                 )}

                 <Button
                   variant="primary"
                   className="w-100"
                   onClick={() => openPreview(crawler.id, crawler.title, crawler.baseUrl)}
                   disabled={previewState.loading && previewState.type === crawler.id}
                 >
                   <FontAwesomeIcon icon={faEye} className="me-2" />
                   Xem & Lấy kết quả
                 </Button>
               </Card.Body>
             </Card>
           </Col>
         ))}
       </Row>

       <h5 className="mb-3 mt-4">Force Sync Kết quả</h5>
       <p className="text-muted small mb-3">Ghi đè kết quả đã lưu, bỏ qua kiểm tra trạng thái đã xử lý</p>
       <Row>
         {forceSyncGames.map((game) => (
           <Col key={game.id} xs={12} md={6} lg={4} className="mb-4">
             <Card className="h-100">
               <Card.Body>
                 <Card.Title>{game.title}</Card.Title>

                 {messages[`force-${game.id}`] && (
                   <Alert
                     variant={messages[`force-${game.id}`].type}
                     className="py-2 px-3 small"
                     dismissible
                     onClose={() => setMessages(prev => ({ ...prev, [`force-${game.id}`]: null }))}
                   >
                     <FontAwesomeIcon
                       icon={messages[`force-${game.id}`].type === 'success' ? faCheckCircle : faExclamationTriangle}
                       className="me-2"
                     />
                     {messages[`force-${game.id}`].text}
                   </Alert>
                 )}

                 <Button
                   variant="warning"
                   className="w-100"
                   onClick={() => forceSync(game.id)}
                   disabled={loading[`force-${game.id}`]}
                 >
                   {loading[`force-${game.id}`] ? (
                     <>
                       <Spinner animation="border" size="sm" className="me-2" />
                       Đang chạy...
                     </>
                   ) : (
                     <>
                       <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                       Force Sync
                     </>
                   )}
                 </Button>
               </Card.Body>
             </Card>
           </Col>
         ))}
       </Row>

       <Row className="mb-4">
         <Col xs={12} className="mb-4">
           <Card>
             <Card.Body>
               <Card.Title>Force Sync Tất cả</Card.Title>

               {messages['force-all'] && (
                 <Alert
                   variant={messages['force-all'].type}
                   className="py-2 px-3 small"
                   dismissible
                   onClose={() => setMessages(prev => ({ ...prev, ['force-all']: null }))}
                 >
                   <FontAwesomeIcon
                     icon={messages['force-all'].type === 'success' ? faCheckCircle : faExclamationTriangle}
                     className="me-2"
                   />
                   <div style={{ whiteSpace: 'pre-wrap' }}>
                     {messages['force-all'].text}
                   </div>
                 </Alert>
               )}

               <Button
                 variant="danger"
                 className="w-100"
                 onClick={() => forceSync('all')}
                 disabled={loading['force-all']}
               >
                 {loading['force-all'] ? (
                   <>
                     <Spinner animation="border" size="sm" className="me-2" />
                     Đang chạy...
                   </>
                 ) : (
                   <>
                     <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                     Force Sync Tất cả
                   </>
                 )}
               </Button>
             </Card.Body>
           </Card>
         </Col>
       </Row>

       <Card className="mt-4">
        <Card.Body>
          <Card.Title>Thông tin</Card.Title>
          <h6 className="mt-3">Vietlott</h6>
          <ul className="mb-3">
            <li><strong>Keno:</strong> Quay mỗi 10 phút từ 6h-22h hàng ngày</li>
            <li><strong>Mega 6/45:</strong> Quay 18h15 vào Chủ nhật, Thứ 4, Thứ 6</li>
            <li><strong>Power 6/55:</strong> Quay 18h15 vào Thứ 3, Thứ 5, Thứ 7</li>
            <li><strong>Max3D:</strong> Quay 18h15 vào Thứ 2, Thứ 4, Thứ 6</li>
            <li><strong>Max3D Pro:</strong> Quay 18h15 vào Thứ 3, Thứ 5, Thứ 7</li>
            <li><strong>Lotto 5/35:</strong> Quay 2 kỳ/ngày lúc 13:00 và 21:00</li>
          </ul>
          <h6>Xổ số Kiến thiết</h6>
          <ul className="mb-0">
            <li><strong>XSMN:</strong> Xổ số Miền Nam quay lúc 16:10-16:30 hàng ngày</li>
            <li><strong>XSMT:</strong> Xổ số Miền Trung quay lúc 17:10-17:30 hàng ngày</li>
            <li>Cronjob tự động chạy sau khi xổ số kết thúc</li>
            <li>Sử dụng nút "Lấy kết quả ngay" nếu cronjob thất bại</li>
          </ul>
        </Card.Body>
      </Card>

      <Modal
        as={Modal.Dialog}
        centered
        size="lg"
        show={previewState.show}
        onHide={closePreview}
      >
        <Modal.Header>
          <Modal.Title className="h6">Preview kết quả: {previewState.title}</Modal.Title>
          <Button variant="close" aria-label="Close" onClick={closePreview} />
        </Modal.Header>
        <Modal.Body>
          {previewState.loading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Đang lấy preview...
            </div>
          )}
          {!previewState.loading && previewState.error && (
            <Alert variant="danger" className="mb-0">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              {previewState.error}
            </Alert>
          )}
          {!previewState.loading && !previewState.error && previewState.data && (
            <PreviewContent type={previewState.type} data={previewState.data} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            className="text-gray ms-auto"
            onClick={closePreview}
            disabled={previewState.applying}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            onClick={applyPreview}
            disabled={previewState.loading || !!previewState.error || !previewState.data || previewState.applying}
          >
            {previewState.applying ? (
              <><Spinner animation="border" size="sm" className="me-2" />Đang apply...</>
            ) : (
              <><FontAwesomeIcon icon={faSync} className="me-2" />Apply (lưu DB)</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const PreviewContent = ({ type, data }) => {
  if (type === 'xsmn' || type === 'xsmt') {
    return (
      <>
        <div className="mb-2 small">
          <strong>Ngày:</strong> {data.resultDate || '-'}{' '}
          <Badge bg="info" className="ms-2">{data.totalParsed}/{data.totalLinks} đài</Badge>
        </div>
        <div className="small text-muted mb-3">
          Nguồn: <a href={data.sourceUrl} target="_blank" rel="noreferrer">{data.sourceUrl}</a>
        </div>
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          <Table striped bordered size="sm" className="small mb-0">
            <thead>
              <tr>
                <th>Đài</th><th>ĐB</th><th>G1</th><th>G2</th><th>G3</th>
                <th>G4</th><th>G5</th><th>G6</th><th>G7</th><th>G8</th>
              </tr>
            </thead>
            <tbody>
              {(data.results || []).map((r, i) => (
                <tr key={i}>
                  <td><strong>{r.channelId}</strong></td>
                  <td>{r.g_db}</td><td>{r.g_1}</td><td>{r.g_2}</td><td>{r.g_3}</td>
                  <td>{r.g_4}</td><td>{r.g_5}</td><td>{r.g_6}</td><td>{r.g_7}</td><td>{r.g_8}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </>
    );
  }

  if (type === 'max3d' || type === 'max3dpro') {
    const groupRows = Array.isArray(data.groupRows) && data.groupRows.length > 0
      ? data.groupRows
      : Object.entries(data.resultGroups || {}).map(([key, numbers]) => ({
        key,
        name: key.toUpperCase(),
        numbers,
      }));

    return (
      <>
        <div className="mb-2 small">
          <strong>Kỳ:</strong> {data.periodId || '-'}
          {data.date && <> &nbsp;|&nbsp; <strong>Ngày:</strong> {data.date}</>}
        </div>
        {data.sourceUrl && (
          <div className="small text-muted mb-3">
            Nguồn: <a href={data.sourceUrl} target="_blank" rel="noreferrer">{data.sourceUrl}</a>
          </div>
        )}
        <Table striped bordered size="sm" className="small mb-0">
          <thead><tr><th>Giải</th><th>Bộ số</th><th>Thưởng</th></tr></thead>
          <tbody>
            {groupRows.map((row) => (
              <tr key={row.key}>
                <td>{row.name || row.key}</td>
                <td>
                  {(row.numbers || []).map((n, i) => (
                    <Badge key={`${row.key}-${i}`} bg="primary" className="me-1" style={{ fontSize: '0.95rem' }}>{n}</Badge>
                  ))}
                </td>
                <td>{row.prize || '-'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <details className="mt-2">
          <summary className="small text-muted">Raw JSON</summary>
          <pre className="small bg-light p-2 mt-2" style={{ maxHeight: 240, overflow: 'auto' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>
      </>
    );
  }

  // Vietlott + xsdt636
  const result = Array.isArray(data.result) ? data.result : [];
  const jackpot = data.jackpot != null ? Number(data.jackpot).toLocaleString() : null;

  return (
    <>
      <div className="mb-2 small">
        <strong>Kỳ:</strong> {data.periodId || '-'}
        {data.openDate && <> &nbsp;|&nbsp; <strong>Ngày:</strong> {data.openDate}</>}
      </div>
      {data.sourceUrl && (
        <div className="small text-muted mb-3">
          Nguồn: <a href={data.sourceUrl} target="_blank" rel="noreferrer">{data.sourceUrl}</a>
        </div>
      )}
      <div className="mb-3">
        <strong className="me-2">Kết quả:</strong>
        {result.map((n, i) => (
          <Badge key={i} bg="primary" className="me-1" style={{ fontSize: '0.95rem' }}>{n}</Badge>
        ))}
      </div>
      {jackpot && (
        <div className="mb-3">
          <strong>Jackpot:</strong> <span className="text-success">{jackpot}</span>
        </div>
      )}
      {data.rawPrizes && data.rawPrizes.length > 0 && (
        <details>
          <summary className="small text-muted">Chi tiết giải thưởng</summary>
          <Table striped bordered size="sm" className="small mt-2 mb-0">
            <thead><tr><th>Giải</th><th>Trùng khớp</th><th>SL</th><th>Giá trị</th></tr></thead>
            <tbody>
              {data.rawPrizes.map((p, i) => (
                <tr key={i}>
                  <td>{p.Giaithuong || p.giaithuong}</td>
                  <td>{p.Trungkhop || p.trungkhop}</td>
                  <td>{p.SoLuong || p.soluong}</td>
                  <td>{p.Giatrigiai || p.giatrigiai}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </details>
      )}
      <details className="mt-2">
        <summary className="small text-muted">Raw JSON</summary>
        <pre className="small bg-light p-2 mt-2" style={{ maxHeight: 240, overflow: 'auto' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </>
  );
};

export default CrawlerSettings;
